import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { companyProfile } from '../content/siteContent';
import { servicesData } from '../content/servicesData';

const quickPrompts = [
  'I need website design',
  'I need visa guidance',
  'I need job placement support',
  'Show contact details'
];

const serviceKeywords = {
  'website-design': ['website', 'web design', 'design', 'landing page', 'portfolio'],
  'web-development': ['development', 'developer', 'web app', 'admin panel', 'backend', 'frontend'],
  'mobile-app-development': ['app', 'android', 'ios', 'mobile app', 'play store'],
  'iot-solutions': ['iot', 'automation', 'sensor', 'device', 'monitoring'],
  'ethical-hacking': ['ethical hacking', 'security', 'cyber', 'penetration', 'vulnerability'],
  'ui-ux-branding': ['ui', 'ux', 'branding', 'brand', 'interface', 'experience'],
  'admission-guidance': ['admission', 'college admission', 'college', 'course selection', 'student guidance'],
  'visa-services': ['visa', 'abroad', 'study abroad', 'work abroad', 'documentation'],
  'job-placement': ['job', 'placement', 'career', 'resume', 'interview'],
  'seo-services': ['seo', 'ranking', 'google', 'search', 'traffic']
};

const normalize = (value) => value.toLowerCase().replace(/\s+/g, ' ').trim();

function findServiceMatch(query) {
  const normalizedQuery = normalize(query);
  let bestMatch = null;
  let bestScore = 0;

  servicesData.forEach((service) => {
    const keywords = serviceKeywords[service.slug] || [];
    const score = keywords.reduce((count, keyword) => {
      return normalizedQuery.includes(keyword) ? count + 1 : count;
    }, 0);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = service;
    }
  });

  return bestScore > 0 ? bestMatch : null;
}

function buildAssistantReply(query) {
  const normalizedQuery = normalize(query);
  const serviceMatch = findServiceMatch(normalizedQuery);

  if (!normalizedQuery) {
    return {
      text: 'Type your question and I will help with services, contact details, or the next best page to open.'
    };
  }

  if (/(hi|hello|hey|namaste|kem cho)/.test(normalizedQuery)) {
    return {
      text: `Hello! I am the quick AI assistant for ${companyProfile.brandName}. You can ask about website design, apps, SEO, visa guidance, job placement, or contact details.`,
      links: [{ to: '/services', label: 'View All Services' }]
    };
  }

  if (/(contact|call|phone|number|email|mail|address|location|office)/.test(normalizedQuery)) {
    return {
      text: `${companyProfile.brandName} is based in ${companyProfile.location}. You can call ${companyProfile.phone} or email ${companyProfile.primaryEmail} to start your inquiry.`,
      links: [
        { to: '/contact', label: 'Open Contact Page' },
        { href: `tel:${companyProfile.phone.replace(/\s+/g, '')}`, label: 'Call Now' },
        { href: `mailto:${companyProfile.primaryEmail}`, label: 'Email Us' }
      ]
    };
  }

  if (/(price|cost|budget|quote|quotation|fees)/.test(normalizedQuery)) {
    return {
      text: 'Pricing depends on the project scope. The best next step is to share your requirement so the team can provide a custom quote.',
      links: [{ to: '/contact', label: 'Request A Quote' }]
    };
  }

  if (/(service|shu karo|what do you do|what services)/.test(normalizedQuery) && !serviceMatch) {
    return {
      text: 'We offer website design, web development, mobile apps, SEO, ethical hacking, IoT solutions, admission guidance, visa guidance, and job placement support.',
      links: [{ to: '/services', label: 'Explore Services' }]
    };
  }

  if (serviceMatch) {
    return {
      text: `Here is the quick answer for ${serviceMatch.menuLabel}: ${serviceMatch.summary}`,
      links: [
        { to: `/services/${serviceMatch.slug}`, label: serviceMatch.ctaLabel },
        { to: '/contact', label: 'Talk To Vedanskrix' }
      ]
    };
  }

  return {
    text: 'I could not match that exactly, but I can still guide you to the most relevant page.',
    links: [
      { to: '/services', label: 'Browse Services' },
      { to: '/contact', label: 'Send Inquiry' },
      { to: '/about', label: 'About Vedanskrix' }
    ]
  };
}

function buildLeadReply(leadForm) {
  const summary = [
    `Name: ${leadForm.name}`,
    `Phone: ${leadForm.phone}`,
    `Email: ${leadForm.email}`,
    leadForm.service ? `Service: ${leadForm.service}` : null,
    leadForm.message ? `Requirement: ${leadForm.message}` : null
  ]
    .filter(Boolean)
    .join('\n');

  const mailSubject = encodeURIComponent(`New inquiry from ${leadForm.name}`);
  const mailBody = encodeURIComponent(summary);
  const whatsappText = encodeURIComponent(
    `Hello Vedanskrix, I am ${leadForm.name}.\nPhone: ${leadForm.phone}\nEmail: ${leadForm.email}${leadForm.service ? `\nService: ${leadForm.service}` : ''}${leadForm.message ? `\nRequirement: ${leadForm.message}` : ''}`
  );

  return {
    userText: `${leadForm.name} shared contact details for ${leadForm.service || 'a new inquiry'}.`,
    assistantText: `Thanks ${leadForm.name}. Your details are captured and the AI chat is now ready. You can continue right away using email, WhatsApp, the contact page, or ask your requirement here.`,
    links: [
      { href: `mailto:${companyProfile.primaryEmail}?subject=${mailSubject}&body=${mailBody}`, label: 'Email Inquiry' },
      { href: `https://wa.me/${companyProfile.whatsapp.replace(/\D/g, '')}?text=${whatsappText}`, label: 'Send On WhatsApp' },
      { to: '/contact', label: 'Open Contact Page' }
    ]
  };
}

function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const promptButtons = useMemo(() => quickPrompts, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = (rawMessage) => {
    const trimmedMessage = rawMessage.trim();

    if (!trimmedMessage) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmedMessage
    };

    const assistantReply = buildAssistantReply(trimmedMessage);

    setMessages((current) => [
      ...current,
      userMessage,
      {
        id: `assistant-${Date.now() + 1}`,
        sender: 'assistant',
        ...assistantReply
      }
    ]);
    setInputValue('');
    setIsOpen(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(inputValue);
  };

  const handleLeadChange = (event) => {
    const { name, value } = event.target;

    setLeadForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleLeadSubmit = (event) => {
    event.preventDefault();

    if (!leadForm.name.trim() || !leadForm.phone.trim() || !leadForm.email.trim()) {
      return;
    }

    const leadReply = buildLeadReply({
      name: leadForm.name.trim(),
      phone: leadForm.phone.trim(),
      email: leadForm.email.trim(),
      service: leadForm.service.trim(),
      message: leadForm.message.trim()
    });

    setMessages((current) => [
      ...current,
      {
        id: `lead-user-${Date.now()}`,
        sender: 'user',
        text: leadReply.userText
      },
      {
        id: `lead-assistant-${Date.now() + 1}`,
        sender: 'assistant',
        text: leadReply.assistantText,
        links: leadReply.links
      }
    ]);
    setLeadCaptured(true);

    setLeadForm({
      name: '',
      phone: '',
      email: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className={isOpen ? 'ai-chat-widget open' : 'ai-chat-widget'}>
      {isOpen ? (
        <section className="ai-chat-panel" aria-label="Vedanskrix AI chat panel">
          <div className="ai-chat-header">
            <div className="ai-chat-brand">
              <img src="/logo.jpg" alt="Vedanskrix logo" />
              <div>
                <strong>Vedanskrix AI Chat</strong>
                <span>{companyProfile.phone} | {companyProfile.primaryEmail}</span>
              </div>
            </div>

            <button
              type="button"
              className="ai-chat-close"
              aria-label="Close AI chat"
              onClick={() => setIsOpen(false)}
            >
              x
            </button>
          </div>

          {!leadCaptured ? (
            <>
              <div className="ai-chat-intro">
                <strong>Start with your details</strong>
                <p>
                  Share your basic details first so the Vedanskrix team can follow up properly.
                  After that, the AI chat will start for your questions.
                </p>
              </div>

              <form className="ai-chat-lead-form first-step" onSubmit={handleLeadSubmit}>
                <div className="ai-chat-form-heading">
                  <strong>Share your details</strong>
                  <span>Enter your name, phone number, and email before starting the chat.</span>
                </div>

                <input
                  type="text"
                  name="name"
                  value={leadForm.name}
                  onChange={handleLeadChange}
                  placeholder="Your name"
                  aria-label="Your name"
                  required
                />

                <input
                  type="tel"
                  name="phone"
                  value={leadForm.phone}
                  onChange={handleLeadChange}
                  placeholder="Phone number"
                  aria-label="Phone number"
                  required
                />

                <input
                  type="email"
                  name="email"
                  value={leadForm.email}
                  onChange={handleLeadChange}
                  placeholder="Email address"
                  aria-label="Email address"
                  required
                />

                <select
                  name="service"
                  value={leadForm.service}
                  onChange={handleLeadChange}
                  aria-label="Service needed"
                >
                  <option value="">Select a service</option>
                  {servicesData.map((service) => (
                    <option key={service.slug} value={service.menuLabel}>
                      {service.menuLabel}
                    </option>
                  ))}
                </select>

                <textarea
                  name="message"
                  value={leadForm.message}
                  onChange={handleLeadChange}
                  placeholder="Tell us what you need"
                  aria-label="Requirement message"
                  rows="3"
                />

                <button type="submit">Start Chat</button>
              </form>
            </>
          ) : (
            <>
              <div className="ai-chat-messages">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={message.sender === 'assistant' ? 'ai-chat-bubble assistant' : 'ai-chat-bubble user'}
                  >
                    <p>{message.text}</p>

                    {message.links?.length ? (
                      <div className="ai-chat-links">
                        {message.links.map((link) => (
                          link.to ? (
                            <Link key={`${message.id}-${link.label}-${link.to}`} to={link.to}>
                              {link.label}
                            </Link>
                          ) : (
                            <a
                              key={`${message.id}-${link.label}-${link.href}`}
                              href={link.href}
                              target={link.href.startsWith('http') ? '_blank' : undefined}
                              rel={link.href.startsWith('http') ? 'noreferrer' : undefined}
                            >
                              {link.label}
                            </a>
                          )
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}

                <div ref={messagesEndRef} />
              </div>

              <div className="ai-chat-prompts">
                {promptButtons.map((prompt) => (
                  <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>

              <form className="ai-chat-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="Ask about services, pricing, or contact..."
                  aria-label="Type your question"
                />
                <button type="submit">Send</button>
              </form>
            </>
          )}
        </section>
      ) : null}

      <button
        type="button"
        className="ai-chat-launcher"
        aria-expanded={isOpen}
        aria-label="Open Vedanskrix AI chat"
        onClick={() => setIsOpen((current) => !current)}
      >
        <img src="/logo.jpg" alt="Vedanskrix logo" />
        <span>AI Chat</span>
      </button>
    </div>
  );
}

export default AiChatWidget;
